import { useState, useMemo } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { Plus, Edit, Trash2, BookOpen, FileText, HelpCircle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin Learn Page
 * Manage learning paths, slides, and quizzes
 */
export default function AdminLearn() {
  const [selectedPathId, setSelectedPathId] = useState<string | null>(null);
  const [isPathDialogOpen, setIsPathDialogOpen] = useState(false);
  const [isSlideDialogOpen, setIsSlideDialogOpen] = useState(false);
  const [isQuizDialogOpen, setIsQuizDialogOpen] = useState(false);

  const utils = trpc.useUtils();

  // Fetch all paths
  const { data: pathsData, isLoading: pathsLoading } = trpc.learnAdmin.allPaths.useQuery();
  const paths = pathsData || []; // Force null-safe array

  // Fetch slides for selected path
  const { data: moduleData } = trpc.learn.slides.useQuery(
    { pathId: selectedPathId! },
    { enabled: !!selectedPathId }
  );

  // Parse module content into slides
  const slides = useMemo(() => {
    if (!moduleData || !moduleData.content_en) return [];
    
    // Split content by slide delimiter (e.g., "---" or "## Slide")
    const slideContents = moduleData.content_en.split(/^---$/gm).filter((s: string) => s.trim());
    
    return slideContents.map((content: string, index: number) => ({
      id: index + 1,
      titleEn: `Slide ${index + 1}`,
      contentEn: content.trim(),
      slideOrder: index + 1,
    }));
  }, [moduleData]);

  // Fetch quizzes for selected path
  const { data: quizzesData } = trpc.learn.quizzes.useQuery(
    { pathId: selectedPathId! },
    { enabled: !!selectedPathId }
  );
  const quizzes = quizzesData || []; // Force null-safe array

  // Mutations
  const createPathMutation = trpc.learnAdmin.createPath.useMutation({
    onSuccess: () => {
      utils.learnAdmin.allPaths.invalidate();
      toast.success("Learning path created successfully");
      setIsPathDialogOpen(false);
    },
  });

  const deletePathMutation = trpc.learnAdmin.deletePath.useMutation({
    onSuccess: () => {
      utils.learnAdmin.allPaths.invalidate();
      toast.success("Learning path deleted successfully");
      setSelectedPathId(null);
    },
  });

  const createSlideMutation = trpc.learnAdmin.createSlide.useMutation({
    onSuccess: () => {
      utils.learn.slides.invalidate();
      toast.success("Slide created successfully");
      setIsSlideDialogOpen(false);
    },
  });

  const deleteSlideMutation = trpc.learnAdmin.deleteSlide.useMutation({
    onSuccess: () => {
      utils.learn.slides.invalidate();
      toast.success("Slide deleted successfully");
    },
  });

  const createQuizMutation = trpc.learnAdmin.createQuiz.useMutation({
    onSuccess: () => {
      utils.learn.quizzes.invalidate();
      toast.success("Quiz created successfully");
      setIsQuizDialogOpen(false);
    },
  });

  const deleteQuizMutation = trpc.learnAdmin.deleteQuiz.useMutation({
    onSuccess: () => {
      utils.learn.quizzes.invalidate();
      toast.success("Quiz deleted successfully");
    },
  });

  const uploadExcelMutation = trpc.learnAdmin.uploadExcel.useMutation({
    onSuccess: (result) => {
      utils.learnAdmin.allPaths.invalidate();
      toast.success(`Excel imported: ${result.modulesCreated} created, ${result.modulesUpdated} updated`);
    },
    onError: (error) => {
      toast.error(`Upload failed: ${error.message}`);
    },
  });

  const deleteAllDataMutation = trpc.learnAdmin.deleteAllData.useMutation({
    onSuccess: (result) => {
      utils.learnAdmin.allPaths.invalidate();
      toast.success(`Deleted: ${result.modulesDeleted} modules, ${result.resultsDeleted} results`);
    },
    onError: (error) => {
      toast.error(`Delete failed: ${error.message}`);
    },
  });

  const updatePathMutation = trpc.learnAdmin.updatePath.useMutation({
    onSuccess: () => {
      utils.learnAdmin.allPaths.invalidate();
      toast.success("Learning path updated successfully");
    },
  });

  const handleCreatePath = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    createPathMutation.mutate({
      title_en: formData.get("titleEn") as string,
      description_en: formData.get("descriptionEn") as string,
      duration_minutes: parseInt(formData.get("duration") as string) || undefined,
      is_published: false,
    });
  };

  const handleTogglePublish = (pathId: string, currentStatus: boolean) => {
    updatePathMutation.mutate({
      id: pathId,
      is_published: !currentStatus,
    });
  };

  const handleExcelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Read file as base64
    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target?.result as string;
      const fileContent = base64.split(',')[1]; // Remove data:application/...;base64, prefix

      uploadExcelMutation.mutate({ fileContent });
    };
    reader.readAsDataURL(file);

    // Reset input
    e.target.value = '';
  };

  const handleDeleteAllData = () => {
    if (!confirm('⚠️ ATTENTION: Cette action supprimera TOUS les modules, slides, questions, résultats de quiz et commentaires. Cette action est IRRÉVERSIBLE. Êtes-vous sûr ?')) {
      return;
    }

    if (!confirm('Dernière confirmation: Voulez-vous vraiment vider toute la base de données learning ?')) {
      return;
    }

    deleteAllDataMutation.mutate();
  };

  const handleCreateSlide = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedPathId) return;

    const formData = new FormData(e.currentTarget);

    createSlideMutation.mutate({
      pathId: selectedPathId.toString(),
      titleEn: formData.get("titleEn") as string,
      contentEn: formData.get("contentEn") as string,
      // TODO: Add other fields to schema
    });
  };

  const handleCreateQuiz = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedPathId) return;

    const formData = new FormData(e.currentTarget);
    const answersEn = [
      formData.get("answer1") as string,
      formData.get("answer2") as string,
      formData.get("answer3") as string,
      formData.get("answer4") as string,
    ].filter(Boolean);

    createQuizMutation.mutate({
      pathId: selectedPathId.toString(),
      questionEn: formData.get("questionEn") as string,
      options: answersEn,
      correctAnswer: parseInt(formData.get("correctAnswerIndex") as string),
      // TODO: Add explanationEn and quizOrder to schema
    });
  };

  const levelColors = {
    beginner: "bg-blue-100 text-blue-800",
    intermediate: "bg-yellow-100 text-yellow-800",
    advanced: "bg-pink-100 text-pink-800",
    expert: "bg-cyan-100 text-cyan-800",
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Learning Management</h1>
            <p className="text-muted-foreground">Manage learning paths, slides, and quizzes</p>
          </div>

          <div className="flex items-center gap-2">
            {/* Excel Upload */}
            <Input
              id="excel-upload"
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleExcelUpload}
            />
            <Button
              variant="outline"
              onClick={() => document.getElementById('excel-upload')?.click()}
              disabled={uploadExcelMutation.isPending}
            >
              {uploadExcelMutation.isPending ? "Uploading..." : "Upload Excel"}
            </Button>

            {/* Delete All Data */}
            <Button
              variant="destructive"
              onClick={handleDeleteAllData}
              disabled={deleteAllDataMutation.isPending}
            >
              {deleteAllDataMutation.isPending ? "Deleting..." : "Vider la base"}
            </Button>

            <Dialog open={isPathDialogOpen} onOpenChange={setIsPathDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                New Learning Path
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Learning Path</DialogTitle>
                <DialogDescription>Add a new learning path for users</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreatePath} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="titleEn">Title (English) *</Label>
                    <Input id="titleEn" name="titleEn" required />
                  </div>
                  <div>
                    <Label htmlFor="titleFr">Title (French)</Label>
                    <Input id="titleFr" name="titleFr" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="descriptionEn">Description (English)</Label>
                  <Textarea id="descriptionEn" name="descriptionEn" rows={3} />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="color">Color *</Label>
                    <Select name="color" required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select color" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="blue">Blue</SelectItem>
                        <SelectItem value="yellow">Yellow</SelectItem>
                        <SelectItem value="pink">Pink</SelectItem>
                        <SelectItem value="cyan">Cyan</SelectItem>
                        <SelectItem value="green">Green</SelectItem>
                        <SelectItem value="purple">Purple</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="level">Level *</Label>
                    <Select name="level" required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">Beginner</SelectItem>
                        <SelectItem value="intermediate">Intermediate</SelectItem>
                        <SelectItem value="advanced">Advanced</SelectItem>
                        <SelectItem value="expert">Expert</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="pathOrder">Order *</Label>
                    <Input
                      id="pathOrder"
                      name="pathOrder"
                      type="number"
                      defaultValue={paths.length + 1}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="durationMinutes">Duration (minutes)</Label>
                    <Input id="durationMinutes" name="durationMinutes" type="number" defaultValue={30} />
                  </div>

                  <div>
                    <Label htmlFor="isPublished">Published *</Label>
                    <Select name="isPublished" defaultValue="false">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Yes</SelectItem>
                        <SelectItem value="false">No (Draft)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsPathDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createPathMutation.isPending}>
                    {createPathMutation.isPending ? "Creating..." : "Create Path"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {/* Learning Paths List */}
          <Card className="md:col-span-1">
            <CardHeader>
              <CardTitle>Learning Paths</CardTitle>
              <CardDescription>{paths.length} paths total</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {pathsLoading ? (
                <p className="text-sm text-muted-foreground">Loading...</p>
              ) : paths.length === 0 ? (
                <p className="text-sm text-muted-foreground">No learning paths yet</p>
              ) : (
                paths.map((path: any) => (
                  <div
                    key={path.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedPathId === path.id
                        ? "border-primary bg-primary/5"
                        : "hover:bg-muted"
                    }`}
                    onClick={() => setSelectedPathId(path.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm">{path.title_en}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          {path.duration_minutes && (
                            <Badge variant="outline" className="text-xs">{path.duration_minutes} min</Badge>
                          )}
                          {path.is_published ? (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700">Published</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Draft</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTogglePublish(path.id, path.is_published);
                          }}
                          title={path.is_published ? "Unpublish" : "Publish"}
                        >
                          {path.is_published ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm("Delete this learning path?")) {
                              deletePathMutation.mutate({ id: path.id });
                            }
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Slides and Quizzes */}
          <Card className="md:col-span-2">
            {selectedPathId ? (
              <Tabs defaultValue="slides">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Content Management</CardTitle>
                      <CardDescription>
                        {(paths.find((p: any) => p.id === selectedPathId) as any)?.titleEn}
                      </CardDescription>
                    </div>
                    <TabsList>
                      <TabsTrigger value="slides">
                        <FileText className="mr-2 h-4 w-4" />
                        Slides
                      </TabsTrigger>
                      <TabsTrigger value="quizzes">
                        <HelpCircle className="mr-2 h-4 w-4" />
                        Quizzes
                      </TabsTrigger>
                    </TabsList>
                  </div>
                </CardHeader>

                <CardContent>
                  <TabsContent value="slides" className="space-y-4">
                    <Dialog open={isSlideDialogOpen} onOpenChange={setIsSlideDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm">
                          <Plus className="mr-2 h-4 w-4" />
                          Add Slide
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Create Slide</DialogTitle>
                          <DialogDescription>Add a new slide to this learning path</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleCreateSlide} className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="slideTitleEn">Title (English) *</Label>
                              <Input id="slideTitleEn" name="titleEn" required />
                            </div>
                            <div>
                              <Label htmlFor="slideTitleFr">Title (French)</Label>
                              <Input id="slideTitleFr" name="titleFr" />
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="contentEn">Content (Markdown) *</Label>
                            <Textarea
                              id="contentEn"
                              name="contentEn"
                              rows={10}
                              placeholder="# Heading&#10;&#10;Your content here..."
                              required
                            />
                          </div>

                          <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => setIsSlideDialogOpen(false)}>
                              Cancel
                            </Button>
                            <Button type="submit" disabled={createSlideMutation.isPending}>
                              {createSlideMutation.isPending ? "Creating..." : "Create Slide"}
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>

                    {slides.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No slides yet</p>
                    ) : (
                      <div className="space-y-2">
                        {slides.map((slide, index) => (
                          <div key={slide.id} className="p-3 rounded-lg border">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline">Slide {index + 1}</Badge>
                                  <h4 className="font-semibold text-sm">{slide.titleEn}</h4>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {slide.contentEn?.substring(0, 100)}...
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => {
                                  if (confirm("Delete this slide?")) {
                                    deleteSlideMutation.mutate({ id: slide.id.toString() });
                                  }
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="quizzes" className="space-y-4">
                    <Dialog open={isQuizDialogOpen} onOpenChange={setIsQuizDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm">
                          <Plus className="mr-2 h-4 w-4" />
                          Add Quiz
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Create Quiz</DialogTitle>
                          <DialogDescription>Add a quiz question to this learning path</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleCreateQuiz} className="space-y-4">
                          <div>
                            <Label htmlFor="questionEn">Question (English) *</Label>
                            <Textarea id="questionEn" name="questionEn" rows={2} required />
                          </div>

                          <div className="space-y-2">
                            <Label>Answers *</Label>
                            <Input name="answer1" placeholder="Answer 1" required />
                            <Input name="answer2" placeholder="Answer 2" required />
                            <Input name="answer3" placeholder="Answer 3" required />
                            <Input name="answer4" placeholder="Answer 4" />
                          </div>

                          <div>
                            <Label htmlFor="correctAnswerIndex">Correct Answer (0-based index) *</Label>
                            <Input
                              id="correctAnswerIndex"
                              name="correctAnswerIndex"
                              type="number"
                              min="0"
                              max="3"
                              defaultValue="0"
                              required
                            />
                          </div>

                          <div>
                            <Label htmlFor="explanationEn">Explanation</Label>
                            <Textarea id="explanationEn" name="explanationEn" rows={2} />
                          </div>

                          <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => setIsQuizDialogOpen(false)}>
                              Cancel
                            </Button>
                            <Button type="submit" disabled={createQuizMutation.isPending}>
                              {createQuizMutation.isPending ? "Creating..." : "Create Quiz"}
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>

                    {quizzes.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No quizzes yet</p>
                    ) : (
                      <div className="space-y-2">
                        {quizzes.map((quiz: any, index: number) => (
                          <div key={quiz.id} className="p-3 rounded-lg border">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline">Q{index + 1}</Badge>
                                  <h4 className="font-semibold text-sm">{quiz.questionEn}</h4>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {((quiz.answersEn as string[]) || []).length} answers
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => {
                                  if (confirm("Delete this quiz?")) {
                                    deleteQuizMutation.mutate({ id: quiz.id });
                                  }
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </CardContent>
              </Tabs>
            ) : (
              <CardContent className="flex items-center justify-center h-96">
                <div className="text-center">
                  <BookOpen className="mx-auto h-12 w-12 text-muted-foreground" />
                  <h3 className="mt-4 text-lg font-semibold">No path selected</h3>
                  <p className="text-sm text-muted-foreground">
                    Select a learning path to manage its content
                  </p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

